export interface AssetDefinition {
  key: AssetType;
  label: string;
  singularLabel: string;
}

export enum AssetType {
  Contacts = 'contacts',
  Origins = 'origins',
  Addresses = 'addresses',
  AddressBook = 'address-book',
  Schedules = 'schedules',
  ReturnSettings = 'returns',
  CarrierAccounts = 'carrier-accounts',
  CarrierSettings = 'carrier-settings',
  CarrierRates = 'carrier-rates',
  CarrierRateSets = 'carrier-rate-sets',
  Contracts = 'contracts',
  LabelProperties = 'label-properties',
  LabelAugmentations = 'label-augmentations',
  LabelAugmentationRules = 'label-augmentation-rules',
  FulfillmentContexts = 'fulfillment-contexts',
  Tenants = 'tenants',
  CarrierSelectionOverrides = 'carrier-selection-overrides',
  ThirdPartyBillingAccounts = 'third-party-billing-accounts',
  ThirdPartyBillingAccountSets = 'third-party-billing-account-sets',
  Tags = 'tags',
  Packaging = 'packaging',
  PackagingSets = 'packaging-sets',
  InjectionSites = 'injection-sites',
  InjectionProfiles = 'injection-profiles',
  ReturnToAddressOverrides = 'return-to-address-overrides',
  DockLaneSortConfigurations = 'shipment-lane-sort-configurations',
  DockLanes = 'shipment-lanes',
  DocumentBlueprints = 'document-blueprints',
}

export enum LedgerAssetType {
  Contacts = 'contacts',
  Origins = 'origins',
  Addresses = 'addresses',
  AddressBook = 'addressBook',
  Schedules = 'schedules',
  ReturnSettings = 'returnsSettings',
  CarrierAccounts = 'carrierAccounts',
  CarrierSettings = 'carrierSettings',
  CarrierRates = 'carrierRates',
  CarrierRateSets = 'carrierRateSets',
  Contracts = 'contracts',
  LabelProperties = 'labelProperties',
  LabelAugmentations = 'labelAugmentations',
  LabelAugmentationRules = 'labelAugmentationRules',
  FulfillmentContexts = 'fulfillmentContexts',
  Tenants = 'tenants',
  CarrierSelectionOverrides = 'carrierSelectionOverrides',
  ThirdPartyBillingAccounts = 'thirdPartyBillingAccounts',
  ThirdPartyBillingAccountSets = 'thirdPartyBillingAccountSets',
  Tags = 'tags',
  Packaging = 'packaging',
  PackagingSets = 'packagingV2Set',
  InjectionSites = 'injectionSite',
  InjectionProfiles = 'injectionProfile',
  ReturnToAddressOverrides = 'returnToAddressOverrides',
  DockLaneSortConfigurations = 'laneSortConfigurations',
  DockLanes = 'lanes',
}

export interface AssetCollection {
  Contacts: AssetDefinition;
  Origins: AssetDefinition;
  Addresses: AssetDefinition;
  AddressBook: AssetDefinition;
  Schedules: AssetDefinition;
  ReturnSettings: AssetDefinition;
  CarrierAccounts: AssetDefinition;
  CarrierSettings: AssetDefinition;
  CarrierRates: AssetDefinition;
  CarrierRateSets: AssetDefinition;
  Contracts: AssetDefinition;
  LabelProperties: AssetDefinition;
  LabelAugmentations: AssetDefinition;
  LabelAugmentationRules: AssetDefinition;
  FulfillmentContexts: AssetDefinition;
  Tenants: AssetDefinition;
  CarrierSelectionOverrides: AssetDefinition;
  ThirdPartyBillingAccounts: AssetDefinition;
  ThirdPartyBillingAccountSets: AssetDefinition;
  Tags: AssetDefinition;
  Packaging: AssetDefinition;
  PackagingSets: AssetDefinition;
  InjectionSites: AssetDefinition;
  InjectionProfiles: AssetDefinition;
  ReturnToAddressOverrides: AssetDefinition;
  DockLaneSortConfigurations: AssetDefinition;
  DockLanes: AssetDefinition;
  DocumentBlueprints: AssetDefinition;
}

export const AssetDefinitions: AssetCollection = {
  Contacts: {
    key: AssetType.Contacts,
    label: 'Contacts',
    singularLabel: 'Contact',
  },
  Origins: {
    key: AssetType.Origins,
    label: 'Origins',
    singularLabel: 'Origin',
  },
  Addresses: {
    key: AssetType.Addresses,
    label: 'Addresses',
    singularLabel: 'Address',
  },
  AddressBook: {
    key: AssetType.AddressBook,
    label: 'Address Book',
    singularLabel: 'Address Book Entry',
  },
  Schedules: {
    key: AssetType.Schedules,
    label: 'Schedules',
    singularLabel: 'Schedule',
  },
  ReturnSettings: {
    key: AssetType.ReturnSettings,
    label: 'Return Settings',
    singularLabel: 'Return Setting',
  },
  CarrierAccounts: {
    key: AssetType.CarrierAccounts,
    label: 'Carrier Accounts',
    singularLabel: 'Carrier Account',
  },
  CarrierSettings: {
    key: AssetType.CarrierSettings,
    label: 'Carrier Settings',
    singularLabel: 'Carrier Setting',
  },
  CarrierRates: {
    key: AssetType.CarrierRates,
    label: 'Carrier Rates',
    singularLabel: 'Carrier Rate',
  },
  CarrierRateSets: {
    key: AssetType.CarrierRateSets,
    label: 'Carrier Rate Sets',
    singularLabel: 'Carrier Rate Set',
  },
  Contracts: {
    key: AssetType.Contracts,
    label: 'Contracts',
    singularLabel: 'Contract',
  },
  LabelProperties: {
    key: AssetType.LabelProperties,
    label: 'Label Properties',
    singularLabel: 'Label Property',
  },
  LabelAugmentations: {
    key: AssetType.LabelAugmentations,
    label: 'Label Augmentations',
    singularLabel: 'Label Augmentation',
  },
  LabelAugmentationRules: {
    key: AssetType.LabelAugmentationRules,
    label: 'Label Augmentation Rules',
    singularLabel: 'Label Augmentation Rule',
  },
  FulfillmentContexts: {
    key: AssetType.FulfillmentContexts,
    label: 'Fulfillment Contexts',
    singularLabel: 'Fulfillment Context',
  },
  Tenants: {
    key: AssetType.Tenants,
    label: 'Tenants',
    singularLabel: 'Tenant',
  },
  CarrierSelectionOverrides: {
    key: AssetType.CarrierSelectionOverrides,
    label: 'Carrier Selection Overrides',
    singularLabel: 'Carrier Selection Override',
  },
  ThirdPartyBillingAccounts: {
    key: AssetType.ThirdPartyBillingAccounts,
    label: 'Third Party Billing Accounts',
    singularLabel: 'Third Party Billing Account',
  },
  ThirdPartyBillingAccountSets: {
    key: AssetType.ThirdPartyBillingAccountSets,
    label: 'Third Party Billing Account Sets',
    singularLabel: 'Third Party Billing Account Set',
  },
  Tags: {
    key: AssetType.Tags,
    label: 'Tags',
    singularLabel: 'Tag',
  },
  Packaging: {
    key: AssetType.Packaging,
    label: 'Packaging',
    singularLabel: 'Package',
  },
  PackagingSets: {
    key: AssetType.PackagingSets,
    label: 'Packaging Sets',
    singularLabel: 'Packaging Set',
  },
  InjectionSites: {
    key: AssetType.InjectionSites,
    label: 'Injection Sites',
    singularLabel: 'Injection Site',
  },
  InjectionProfiles: {
    key: AssetType.InjectionProfiles,
    label: 'Injection Profiles',
    singularLabel: 'Injection Profile',
  },
  ReturnToAddressOverrides: {
    key: AssetType.ReturnToAddressOverrides,
    label: 'Return To Address Overrides',
    singularLabel: 'Return To Address Override',
  },
  DockLaneSortConfigurations: {
    key: AssetType.DockLaneSortConfigurations,
    label: 'Carrier Shipment Lane Configurations',
    singularLabel: 'Carrier Shipment Lane Configuration',
  },
  DockLanes: {
    key: AssetType.DockLanes,
    label: 'Shipment Lanes',
    singularLabel: 'Shipment Lane',
  },
  DocumentBlueprints: {
    key: AssetType.DocumentBlueprints,
    label: 'Document Blueprints',
    singularLabel: 'Document Blueprint',
  },
};
